import { useState, useRef, useEffect } from "react";

const API_BASE = "https://capitaliq-backend-production-bff2.up.railway.app";

async function enrichAsset(description, amount) {
  const r = await fetch(`${API_BASE}/api/enrich`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ description, amount })
  });
  const d = await r.json();
  if (!r.ok) throw new Error(d.error || "Enrichment failed");
  return d;
}

const c = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(160deg, #f0f7ff 0%, #fafbff 50%, #f5f0ff 100%)",
    fontFamily: "'DM Sans', sans-serif",
    display: "flex", flexDirection: "column", alignItems: "center"
  },
  header: {
    width: "100%", padding: "16px 24px",
    display: "flex", alignItems: "center", justifyContent: "space-between",
    background: "rgba(255,255,255,0.85)", backdropFilter: "blur(8px)",
    borderBottom: "1px solid rgba(59,125,247,0.1)", boxSizing: "border-box"
  },
  logoWrap: { display: "flex", alignItems: "center", gap: 10 },
  logoIcon: {
    width: 32, height: 32, borderRadius: 8,
    background: "linear-gradient(135deg,#3b7df7,#6d48e5)",
    display: "flex", alignItems: "center", justifyContent: "center"
  },
  logoText: { color: "#1a2840", fontSize: 16, fontWeight: 700, letterSpacing: "-0.02em" },
  logoSub: { color: "#94a3b8", fontSize: 10, fontWeight: 600, letterSpacing: "0.05em", textTransform: "uppercase" },
  backBtn: {
    background: "none", border: "1px solid #e2e8f0", borderRadius: 8,
    color: "#64748b", fontSize: 13, padding: "6px 14px", cursor: "pointer"
  },
  main: { width: "100%", maxWidth: 520, padding: "32px 20px 80px", flex: 1 },
  bubble: {
    background: "#fff", border: "1px solid #e8f0fe",
    borderRadius: "18px 18px 18px 4px", padding: "14px 18px",
    color: "#1a2840", fontSize: 14, lineHeight: 1.75, marginBottom: 12,
    boxShadow: "0 2px 12px rgba(59,125,247,0.06)"
  },
  userBubble: {
    background: "linear-gradient(135deg,#3b7df7,#6d48e5)",
    borderRadius: "18px 18px 4px 18px", padding: "11px 16px",
    color: "#fff", fontSize: 14, lineHeight: 1.6, marginBottom: 12,
    alignSelf: "flex-end", maxWidth: "80%"
  },
  botRow: { display: "flex", gap: 10, alignItems: "flex-end", marginBottom: 2 },
  avatar: {
    width: 26, height: 26, borderRadius: 7, flexShrink: 0,
    background: "linear-gradient(135deg,#3b7df7,#6d48e5)",
    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11
  },
  input: {
    width: "100%", padding: "13px 16px", borderRadius: 12,
    border: "1.5px solid #e2e8f0", background: "#fff",
    color: "#1a2840", fontSize: 15, fontFamily: "'DM Sans', sans-serif",
    outline: "none", boxSizing: "border-box",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)"
  },
  btnRow: { display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 },
  btnPrimary: {
    padding: "12px 24px", borderRadius: 12, border: "none",
    background: "linear-gradient(135deg,#3b7df7,#6d48e5)",
    color: "#fff", fontSize: 14, fontWeight: 600, cursor: "pointer",
    boxShadow: "0 4px 12px rgba(59,125,247,0.25)"
  },
  btnSecondary: {
    padding: "12px 20px", borderRadius: 12,
    border: "1.5px solid #e2e8f0", background: "#fff",
    color: "#64748b", fontSize: 14, fontWeight: 500, cursor: "pointer"
  },
  btnOption: (selected) => ({
    padding: "16px 18px", borderRadius: 14, cursor: "pointer",
    border: `2px solid ${selected ? '#3b7df7' : '#e2e8f0'}`,
    background: selected ? "linear-gradient(135deg,#eff6ff,#f5f0ff)" : "#fff",
    color: selected ? "#3b7df7" : "#1a2840",
    fontSize: 14, fontWeight: selected ? 600 : 400,
    textAlign: "left", transition: "all 0.15s",
    boxShadow: selected ? "0 4px 12px rgba(59,125,247,0.12)" : "none"
  }),
  card: {
    background: "#fff", border: "1px solid #e8f0fe", borderRadius: 14,
    padding: 20, marginBottom: 16, boxShadow: "0 2px 8px rgba(59,125,247,0.06)"
  },
  cardLabel: { color: "#94a3b8", fontSize: 10, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 3 },
  cardValue: { color: "#1a2840", fontSize: 13, fontWeight: 500, lineHeight: 1.4 },
  editLink: { color: "#3b7df7", fontSize: 12, cursor: "pointer", textDecoration: "underline", marginTop: 3, display: "inline-block" },
  success: {
    background: "linear-gradient(135deg,#f0fdf4,#ecfdf5)",
    border: "1px solid #86efac", borderRadius: 16, padding: 28, textAlign: "center"
  },
};

const STEPS = {
  NAME: "name", MOBILE: "mobile", EMAIL: "email",
  TYPE: "type", ASSET_DESC: "asset_desc", ENRICHING: "enriching",
  AMOUNT: "amount", NOTES: "notes", SUMMARY: "summary", DONE: "done"
};

function BotMessage({ text }) {
  return (
    <div style={c.botRow}>
      <div style={c.avatar}>⚡</div>
      <div style={c.bubble}>
        {text.split('\n').map((line, i, arr) => (
          <span key={i}>{line}{i < arr.length - 1 && <br/>}</span>
        ))}
      </div>
    </div>
  );
}

function UserMessage({ text }) {
  return (
    <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
      <div style={c.userBubble}>{text}</div>
    </div>
  );
}

function ConsumerAssetCard({ data }) {
  const fields = [
    ["Asset Type", data.assetType],
    ["Category", data.category],
    ["Make", data.make],
    ["Model", data.model],
    ["Year / Age", data.year ? `${data.year}${data.age ? ` (~${data.age}yr)` : ""}` : data.age ? `~${data.age} years old` : null],
    ["Condition", data.condition],
    ["Specs", data.engineOrCapacity],
    ["Licence Required", data.licenceRequired],
    ["Primary Use", data.primaryUse],
  ].filter(([, v]) => v && v !== "null" && v !== "Unknown" && v !== "None");

  const cColor = { High: "#16a34a", Medium: "#d97706", Low: "#dc2626" }[data.confidence] || "#64748b";
  const cBg = { High: "#f0fdf4", Medium: "#fffbeb", Low: "#fef2f2" }[data.confidence] || "#f8faff";

  return (
    <div style={{
      background: "#fff", border: "1px solid #e8f0fe", borderRadius: 14,
      padding: 18, marginBottom: 12, boxShadow: "0 2px 12px rgba(59,125,247,0.08)"
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
        <span style={{ color: "#3b7df7", fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase" }}>
          ⚡ Asset Profile
        </span>
        <span style={{
          fontSize: 11, fontWeight: 600, color: cColor,
          background: cBg, padding: "3px 10px", borderRadius: 20,
          border: `1px solid ${cColor}40`
        }}>{data.confidence} confidence</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px 18px", marginBottom: data.lenderNotes ? 14 : 0 }}>
        {fields.map(([label, val]) => (
          <div key={label}>
            <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
            <div style={{ color: "#1a2840", fontSize: 13, fontWeight: 500 }}>{val}</div>
          </div>
        ))}
      </div>
      {data.lenderNotes && (
        <div style={{ background: "#f8faff", border: "1px solid #e8f0fe", borderRadius: 9, padding: "10px 13px" }}>
          <div style={{ color: "#94a3b8", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 5 }}>Finance Notes</div>
          <p style={{ color: "#64748b", fontSize: 12, lineHeight: 1.65, margin: 0 }}>{data.lenderNotes}</p>
        </div>
      )}
    </div>
  );
}

export default function ConsumerFlow({ onBack }) {
  const [step, setStep] = useState(STEPS.NAME);
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [financeType, setFinanceType] = useState("");
  const [assetDesc, setAssetDesc] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [enriched, setEnriched] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);
  const [editingField, setEditingField] = useState(null);
  const [showAssetCard, setShowAssetCard] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [history, step]);

  const addHistory = (role, text) => setHistory(h => [...h, { role, text }]);

  const fmt = (n) => `$${parseInt((n || "0").toString().replace(/,/g, "")).toLocaleString("en-AU")}`;

  const formatCurrency = (v) => {
    const n = v.replace(/\D/g, "");
    return n ? parseInt(n).toLocaleString("en-AU") : "";
  };

  const handleNext = async (currentStep, value) => {
    switch (currentStep) {
      case STEPS.NAME:
        addHistory("user", value);
        addHistory("bot", `Lovely to meet you, ${value.split(" ")[0]}! 😊\n\nWhat's the best mobile number to reach you on?`);
        setStep(STEPS.MOBILE);
        break;
      case STEPS.MOBILE:
        addHistory("user", value);
        addHistory("bot", `Thanks! And your email address?`);
        setStep(STEPS.EMAIL);
        break;
      case STEPS.EMAIL:
        addHistory("user", value);
        addHistory("bot", `Great — what are you looking to finance today?`);
        setStep(STEPS.TYPE);
        break;
      case STEPS.TYPE:
        setFinanceType(value);
        addHistory("user", value === "vehicle" ? "🚗 Vehicle Finance" : "🏗️ Plant & Equipment");
        addHistory("bot", value === "vehicle"
          ? `🚗 Great choice!\n\nDescribe the vehicle you're looking to finance — make, model, year, new or used, and what you'll be using it for.`
          : `🏗️ No problem!\n\nDescribe the equipment you're looking to finance — type, make, model, age and what it's used for.`
        );
        setStep(STEPS.ASSET_DESC);
        break;
      case STEPS.ASSET_DESC:
        addHistory("user", value);
        addHistory("bot", `On it — let me analyse that for you…`);
        setStep(STEPS.ENRICHING);
        try {
          const result = await enrichAsset(value, "100000");
          setEnriched(result);
          setShowAssetCard(true);
          addHistory("bot", `Here's what I've put together on that ${financeType === "vehicle" ? "vehicle" : "asset"}:`);
          addHistory("asset_card", result);
          addHistory("bot", result.confidence === "High"
            ? `Everything checks out — I have a clear picture of what you're after. 💪\n\nNow, how much are you looking to borrow?`
            : `I have a good picture of the asset. ${result.clarifyingQuestions?.length ? `You may want to mention: ${result.clarifyingQuestions[0]}` : ""}\n\nHow much are you looking to borrow?`
          );
        } catch (e) {
          addHistory("bot", `Got it! How much are you looking to borrow?`);
        }
        setStep(STEPS.AMOUNT);
        break;
      case STEPS.AMOUNT:
        addHistory("user", fmt(value.replace(/,/g, "")));
        addHistory("bot", `Almost there! Is there anything else you'd like us to know — any timeframes, special requirements or circumstances? (Feel free to skip this.)`);
        setStep(STEPS.NOTES);
        break;
      case STEPS.NOTES:
        if (value.trim()) addHistory("user", value);
        addHistory("bot", `Perfect — here's your summary. Please check everything looks right before I send this through.`);
        setStep(STEPS.SUMMARY);
        break;
      default:
        break;
    }
  };

  const handleEdit = (field) => setEditingField(field);

  const handleEditSave = (field, value) => {
    switch(field) {
      case 'name': setName(value); break;
      case 'mobile': setMobile(value); break;
      case 'email': setEmail(value); break;
      case 'amount': setAmount(formatCurrency(value.replace(/,/g,"").replace(/\$/g,""))); break;
      case 'notes': setNotes(value); break;
      case 'asset': setAssetDesc(value); setEnriched(null); break;
      default: break;
    }
    setEditingField(null);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const rawAmount = amount.replace(/,/g, "");

    let assetData = enriched;
    if (!assetData && assetDesc) {
      try {
        assetData = await enrichAsset(assetDesc, rawAmount || "100000");
        setEnriched(assetData);
      } catch (e) { console.error(e); }
    }

    const notesText = [
      notes,
      assetData ? [
        `AI Asset Profile:`,
        `Type: ${assetData.assetType || "N/A"}`,
        `Category: ${assetData.category || "N/A"}`,
        assetData.make ? `Make: ${assetData.make}` : null,
        assetData.model ? `Model: ${assetData.model}` : null,
        assetData.year ? `Year: ${assetData.year}` : null,
        `Condition: ${assetData.condition || "N/A"}`,
        assetData.age ? `Est. Age: ${assetData.age} yrs` : null,
        assetData.engineOrCapacity ? `Engine/Capacity: ${assetData.engineOrCapacity}` : null,
        assetData.licenceRequired ? `Licence Required: ${assetData.licenceRequired}` : null,
        assetData.primaryUse ? `Primary Use: ${assetData.primaryUse}` : null,
        assetData.lenderNotes ? `Lender Notes: ${assetData.lenderNotes}` : null,
      ].filter(Boolean).join(" | ") : null
    ].filter(Boolean).join("\n\n");

    try {
      await fetch(`${API_BASE}/api/consumer-submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name, mobile, email, financeType,
          amount: rawAmount,
          vehicle: financeType === "vehicle" ? {
            make: assetData?.make || "",
            model: assetData?.model || "",
            condition: assetData?.condition || "",
            description: assetDesc
          } : null,
          asset: { description: assetDesc, ...assetData },
          notes: notesText
        })
      });
      setStep(STEPS.DONE);
    } catch (e) {
      console.error(e);
    }
    setSubmitting(false);
  };

  const summaryFields = [
    { key: "name", label: "Full Name", value: name },
    { key: "mobile", label: "Mobile", value: mobile },
    { key: "email", label: "Email", value: email },
    { key: "type", label: "Finance Type", value: financeType === "vehicle" ? "🚗 Vehicle Finance" : "🏗️ Plant & Equipment", noEdit: true },
    { key: "asset", label: financeType === "vehicle" ? "Vehicle Description" : "Asset Description", value: assetDesc },
    { key: "amount", label: "Amount Sought", value: fmt(amount) },
    ...(notes ? [{ key: "notes", label: "Notes", value: notes }] : [])
  ];

  return (
    <div style={c.page}>
      <div style={c.header}>
        <div style={c.logoWrap}>
          <div style={c.logoIcon}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
            </svg>
          </div>
          <div>
            <div style={c.logoText}>CapitalIQ</div>
            <div style={c.logoSub}>Personal Finance</div>
          </div>
        </div>
        <button style={c.backBtn} onClick={onBack}>← Back</button>
      </div>

      <div style={c.main}>
        {history.map((msg, i) => (
          msg.role === "bot" ? <BotMessage key={i} text={msg.text} />
          : msg.role === "asset_card" ? <ConsumerAssetCard key={i} data={msg.text} />
          : <UserMessage key={i} text={msg.text} />
        ))}

        {step === STEPS.NAME && (
          <div>
            <BotMessage text={`👋 Welcome to CapitalIQ Personal Finance!\n\nI'll help you lodge a finance enquiry in just a few minutes.\n\nLet's start — what's your full name?`} />
            <input style={c.input} value={name} onChange={e => setName(e.target.value)}
              placeholder="e.g. John Smith" autoFocus
              onKeyDown={e => e.key === "Enter" && name.trim() && handleNext(STEPS.NAME, name)} />
            <div style={c.btnRow}>
              <button style={c.btnPrimary} onClick={() => name.trim() && handleNext(STEPS.NAME, name)} disabled={!name.trim()}>Continue →</button>
            </div>
          </div>
        )}

        {step === STEPS.MOBILE && (
          <div>
            <input style={c.input} value={mobile} onChange={e => setMobile(e.target.value)}
              placeholder="e.g. 0412 345 678" type="tel" autoFocus
              onKeyDown={e => e.key === "Enter" && mobile.trim() && handleNext(STEPS.MOBILE, mobile)} />
            <div style={c.btnRow}>
              <button style={c.btnPrimary} onClick={() => mobile.trim() && handleNext(STEPS.MOBILE, mobile)} disabled={!mobile.trim()}>Continue →</button>
            </div>
          </div>
        )}

        {step === STEPS.EMAIL && (
          <div>
            <input style={c.input} value={email} onChange={e => setEmail(e.target.value)}
              placeholder="e.g. john@email.com" type="email" autoFocus
              onKeyDown={e => e.key === "Enter" && email.trim() && handleNext(STEPS.EMAIL, email)} />
            <div style={c.btnRow}>
              <button style={c.btnPrimary} onClick={() => email.trim() && handleNext(STEPS.EMAIL, email)} disabled={!email.trim()}>Continue →</button>
            </div>
          </div>
        )}

        {step === STEPS.TYPE && (
          <div style={c.btnRow}>
            <button style={{...c.btnOption(false), flex: 1}} onClick={() => handleNext(STEPS.TYPE, "vehicle")}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>🚗</div>
              <div style={{ fontWeight: 600, marginBottom: 3 }}>Vehicle Finance</div>
              <div style={{ fontSize: 12, color: "#64748b" }}>Car, ute, van, motorcycle</div>
            </button>
            <button style={{...c.btnOption(false), flex: 1}} onClick={() => handleNext(STEPS.TYPE, "plant")}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>🏗️</div>
              <div style={{ fontWeight: 600, marginBottom: 3 }}>Plant & Equipment</div>
              <div style={{ fontSize: 12, color: "#64748b" }}>Machinery, tools, trailers</div>
            </button>
          </div>
        )}

        {step === STEPS.ASSET_DESC && (
          <div>
            <textarea style={{...c.input, minHeight: 85, resize: "vertical"}}
              value={assetDesc} onChange={e => setAssetDesc(e.target.value)}
              placeholder={financeType === "vehicle"
                ? "e.g. 2023 Toyota HiLux SR5 4WD dual cab, new, used for work"
                : "e.g. 2020 Kubota tractor, 4WD, 60hp, hobby farming"} autoFocus />
            <div style={c.btnRow}>
              <button style={c.btnPrimary}
                onClick={() => assetDesc.trim() && handleNext(STEPS.ASSET_DESC, assetDesc)}
                disabled={!assetDesc.trim()}>
                Analyse →
              </button>
            </div>
          </div>
        )}

        {step === STEPS.ENRICHING && (
          <div style={{ color: "#64748b", fontSize: 14, padding: "12px 0" }}>
            ⏳ Analysing asset details…
          </div>
        )}

        {step === STEPS.AMOUNT && (
          <div>
            <div style={{ position: "relative" }}>
              <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontWeight: 600 }}>$</span>
              <input style={{...c.input, paddingLeft: 28}} value={amount}
                onChange={e => setAmount(formatCurrency(e.target.value))}
                placeholder="50,000" autoFocus
                onKeyDown={e => e.key === "Enter" && amount.trim() && handleNext(STEPS.AMOUNT, amount)} />
            </div>
            <div style={c.btnRow}>
              <button style={c.btnPrimary} onClick={() => amount.trim() && handleNext(STEPS.AMOUNT, amount)} disabled={!amount.trim()}>Continue →</button>
            </div>
          </div>
        )}

        {step === STEPS.NOTES && (
          <div>
            <textarea style={{...c.input, minHeight: 80, resize: "vertical"}}
              value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Any additional details… (optional)" autoFocus />
            <div style={c.btnRow}>
              <button style={c.btnPrimary} onClick={() => handleNext(STEPS.NOTES, notes)}>
                {notes.trim() ? "Continue →" : "Skip →"}
              </button>
            </div>
          </div>
        )}

        {step === STEPS.SUMMARY && (
          <div>
            <div style={c.card}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 20px" }}>
                {summaryFields.map(f => (
                  <div key={f.key} style={{ gridColumn: f.key === "notes" || f.key === "asset" ? "1/-1" : "auto" }}>
                    {editingField === f.key ? (
                      <div>
                        <div style={c.cardLabel}>{f.label}</div>
                        {f.key === "notes" || f.key === "asset" ? (
                          <textarea style={{...c.input, minHeight: 70, resize: "vertical", fontSize: 13}}
                            defaultValue={f.key === "notes" ? notes : assetDesc}
                            autoFocus
                            onBlur={e => handleEditSave(f.key, e.target.value)} />
                        ) : (
                          <input style={{...c.input, fontSize: 13}}
                            defaultValue={f.key === "amount" ? amount : f.value}
                            autoFocus
                            onBlur={e => handleEditSave(f.key, e.target.value)}
                            onKeyDown={e => e.key === "Enter" && handleEditSave(f.key, e.target.value)} />
                        )}
                      </div>
                    ) : (
                      <div>
                        <div style={c.cardLabel}>{f.label}</div>
                        <div style={c.cardValue}>{f.value}</div>
                        {!f.noEdit && <span style={c.editLink} onClick={() => handleEdit(f.key)}>edit</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            {!editingField && (
              <div style={c.btnRow}>
                <button style={c.btnPrimary} onClick={handleSubmit} disabled={submitting}>
                  {submitting ? "Submitting…" : "✓ Submit Enquiry →"}
                </button>
              </div>
            )}
          </div>
        )}

        {step === STEPS.DONE && (
          <div style={c.success}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>🎉</div>
            <div style={{ color: "#166534", fontSize: 20, fontWeight: 700, marginBottom: 10, fontFamily: "'Playfair Display', serif" }}>
              Enquiry Submitted!
            </div>
            <div style={{ color: "#4b7a5e", fontSize: 14, lineHeight: 1.75, marginBottom: 20 }}>
              Thank you, {name.split(" ")[0]}! Your finance enquiry has been received.<br/><br/>
              One of our team will be in touch within 1 business day to discuss your options.<br/><br/>
              Need to speak sooner? Call us on <strong>📞 1800 00 25 25</strong>
            </div>
            <button style={{...c.btnPrimary, width: "100%"}} onClick={onBack}>← Back to Home</button>
          </div>
        )}

        <div ref={bottomRef} />
      </div>
    </div>
  );
}
